/**
 * Image URL utilities for handling relative and absolute image paths
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Convert a potentially relative image URL to an absolute URL
 * Handles:
 * - Relative paths: /api/uploads/images/... -> https://domain/api/uploads/images/...
 * - Already absolute URLs: https://... -> unchanged
 * - External URLs (unsplash, etc): unchanged
 * - Null/undefined: returns placeholder
 * 
 * @param {string} url - The image URL (relative or absolute)
 * @param {string} placeholder - Fallback image URL
 * @returns {string} - Absolute image URL
 */
export const getImageUrl = (url, placeholder = null) => {
  if (!url) {
    return placeholder;
  }
  
  // Already absolute URL (external or full)
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // Data URL (base64)
  if (url.startsWith('data:')) {
    return url;
  }
  
  // Relative URL - prepend API_URL
  if (url.startsWith('/')) {
    return `${API_URL}${url}`;
  }
  
  // Fallback - assume it's a relative path
  return `${API_URL}/${url}`;
};

/**
 * Get first image from an images array with fallback
 * 
 * @param {string[]} images - Array of image URLs
 * @param {string} placeholder - Fallback image URL
 * @returns {string} - First image URL or placeholder
 */
export const getFirstImage = (images, placeholder = null) => {
  if (!images || !Array.isArray(images) || images.length === 0) {
    return placeholder;
  }
  
  return getImageUrl(images[0], placeholder);
};

/**
 * Convert all images in an array to absolute URLs
 * 
 * @param {string[]} images - Array of image URLs
 * @returns {string[]} - Array of absolute image URLs
 */
export const getAbsoluteImageUrls = (images) => {
  if (!images || !Array.isArray(images)) {
    return [];
  }
  
  return images.map(url => getImageUrl(url)).filter(Boolean);
};

export default {
  getImageUrl,
  getFirstImage,
  getAbsoluteImageUrls
};
